import { createContext } from "react";
import { CategoryStore } from "@/stores/CategoryStore";
import { PodcastStore } from "@/stores/PodcastStore";
import { SearchStore } from "@/stores/SearchStore";
import { PlayerStore } from "@/stores/PlayerStore";
import { EpisodeStore } from "@/stores/EpisodeStore";
import { AuthorStore } from "@/stores/AuthorStore";
import { UserStore } from "@/stores/UserStore";
import { LanguageStore } from "@/stores/LanguageStore";
import { AppStore } from "@/stores/AppStore";
import { SubjectStore } from "@/stores/SubjectStore";
import { AdStore } from "@/stores/AdStore";
import { CollectionStore } from "@/stores/CollectionStore";
import { PlaylistStore } from "@/stores/PlaylistStore";
import { ListsStore } from "@/stores/ListsStore";
import { SettingsStore } from "@/stores/SettingsStore";
import { HistoryStore } from "@/stores/HistoryStore";
import { ImportStore } from "@/stores/ImportStore";
import { ToplistStore } from "@/stores/ToplistStore";
import { AuthStore } from "@/stores/AuthStore";
import { FollowsStore } from "@/stores/FollowsStore";
import { ListeningStore } from "@/stores/ListeningStore";
import { SleepTimerStore } from "@/stores/SleepTimerStore";
import { FeedStore } from "@/stores/FeedStore";
import { LocalImportStore } from "@/stores/LocalImportStore";
import { showDeviceFollowsNotice } from "@/components/auth/DeviceFollowsNotice";
import { when } from "mobx";
import { notifications } from "@mantine/notifications";
import { resumeAfterSignIn } from "@/core/signInIntent";
import { createClient } from "@/core/helpers";
import { createAuthorsApi } from "@/api/authors";
import { createCategoriesApi } from "@/api/categories";
import { createSearchApi } from "@/api/search";
import { createTrackingApi } from "@/api/tracking";
import { createCollectionsApi } from "@/api/collections";
import { createEpisodesApi } from "@/api/episodes";
import { createPodcastsApi } from "@/api/podcasts";
import { createSubjectsApi } from "@/api/subjects";
import { createToplistApi } from "@/api/toplist";

// One client for the catalogue stores: they share its cache and its in-flight requests
const catalogueClient = createClient();
const podcastStore = new PodcastStore(createPodcastsApi(catalogueClient));
const categoryStore = new CategoryStore(createCategoriesApi(catalogueClient));
const subjectStore = new SubjectStore(createSubjectsApi(catalogueClient));
const searchStore = new SearchStore(createSearchApi(catalogueClient));
const trackingApi = createTrackingApi(catalogueClient);
export const playerStore = new PlayerStore(trackingApi);
const episodeStore = new EpisodeStore(createEpisodesApi(catalogueClient));
const authorStore = new AuthorStore(createAuthorsApi(catalogueClient));
const userStore = new UserStore(createEpisodesApi(catalogueClient), createPodcastsApi(catalogueClient), trackingApi);
const languageStore = new LanguageStore(createPodcastsApi(catalogueClient), createEpisodesApi(catalogueClient));
const appStore = new AppStore();
const adStore = new AdStore();
const collectionStore = new CollectionStore(createCollectionsApi(catalogueClient));
const toplistStore = new ToplistStore(createToplistApi(catalogueClient));
export const authStore = new AuthStore();
export const playlistStore = new PlaylistStore(createClient(), authStore);
export const listsStore = new ListsStore(createClient(), authStore);
export const settingsStore = new SettingsStore(createClient(), authStore);
export const importStore = new ImportStore(createClient());
export const followsStore = new FollowsStore(createClient(), authStore);
export const listeningStore = new ListeningStore(createClient(), authStore);
export const historyStore = new HistoryStore(createClient());
export const feedStore = new FeedStore(createClient(), authStore, followsStore);
export const sleepTimerStore = new SleepTimerStore();

// Moves what the browser kept before accounts onto the account at sign-in
export const localImportStore = new LocalImportStore(
    createClient(),
    authStore,
    async () => {
        await Promise.all([followsStore.load(), playlistStore.reload(), listeningStore.load(), feedStore.load()]);
    },
    () =>
        notifications.show({
            title: "Klart",
            message: "Vi flyttade det du sparat på den här enheten till ditt konto.",
            autoClose: 8000,
        }),
    (count, choose) => showDeviceFollowsNotice(count, choose)
);

// What the listener was trying when they were asked to sign in (e.g. following a podcast) happens once
// they are in and the account's own lists are loaded, so a reload cannot undo it
resumeAfterSignIn({
    isSignedIn: () => authStore.status === "authenticated",
    ready: async () => {
        await localImportStore.whenIdle();
        await when(() => followsStore.loaded, { timeout: 10_000 }).catch(() => undefined);
    },
    take: () => appStore.takeSignInIntent(),
    notify: (message) => notifications.show({ message, autoClose: 4000 }),
});

export const stores = createContext({
    podcastStore,
    categoryStore,
    searchStore,
    playerStore,
    episodeStore,
    authorStore,
    userStore,
    languageStore,
    appStore,
    subjectStore,
    adStore,
    collectionStore,
    playlistStore,
    listsStore,
    settingsStore,
    importStore,
    historyStore,
    toplistStore,
    authStore,
    listeningStore,
    followsStore,
    feedStore,
    sleepTimerStore,
});
